/* Subscribe to a call's lifecycle SSE and surface the orchestrator
 * fields the canvas renders: current_stage, detected_intent + confidence,
 * intent_overridden_at.
 *
 * Uses the existing /api/calls/:id and /api/calls/:id/stream endpoints —
 * no new backend surface. The bot writes these on lifecycle events; the
 * worker emits row updates via the same SSE the call detail panes use. */

import { useEffect, useState } from "react";

import { API_URL } from "../lib/api";

export type Stage = "start" | "discovery" | "offer" | "close";

export type Intent =
  | "bill_shock"
  | "competitor_switch"
  | "service_issue"
  | "cancel_no_save"
  | "other";

const INTENT_VALUES: readonly Intent[] = [
  "bill_shock",
  "competitor_switch",
  "service_issue",
  "cancel_no_save",
  "other",
];

export type Outcome = "saved" | "churned" | "escalated" | "unresolved";

const OUTCOME_VALUES: readonly Outcome[] = [
  "saved",
  "churned",
  "escalated",
  "unresolved",
];

export type CallOrchState = {
  stage: Stage | null;
  intent: Intent | null;
  intentConfidence: number | null;
  intentOverriddenAt: string | null;
  outcome: Outcome | null;
  endedAt: string | null;
};

const EMPTY: CallOrchState = {
  stage: null,
  intent: null,
  intentConfidence: null,
  intentOverriddenAt: null,
  outcome: null,
  endedAt: null,
};

function parseStage(v: unknown): Stage | null {
  if (v === "start" || v === "discovery" || v === "offer" || v === "close") {
    return v;
  }
  return null;
}

function parseIntent(v: unknown): Intent | null {
  return typeof v === "string" && (INTENT_VALUES as readonly string[]).includes(v)
    ? (v as Intent)
    : null;
}

function parseOutcome(v: unknown): Outcome | null {
  return typeof v === "string" && (OUTCOME_VALUES as readonly string[]).includes(v)
    ? (v as Outcome)
    : null;
}

function parseConfidence(v: unknown): number | null {
  if (typeof v !== "number") return null;
  if (!Number.isFinite(v)) return null;
  return Math.max(0, Math.min(1, v));
}

type CallRow = {
  current_stage?: string;
  detected_intent?: string;
  intent_confidence?: number;
  intent_overridden_at?: string | null;
  outcome?: string | null;
  ended_at?: string | null;
};

function applyRow(prev: CallOrchState, row: CallRow): CallOrchState {
  const stage = parseStage(row.current_stage);
  const intent = parseIntent(row.detected_intent);
  const confidence = parseConfidence(row.intent_confidence);
  const outcome = parseOutcome(row.outcome);
  return {
    stage:               stage              ?? prev.stage,
    intent:              intent             ?? prev.intent,
    intentConfidence:    confidence         ?? prev.intentConfidence,
    intentOverriddenAt:
      typeof row.intent_overridden_at === "string"
        ? row.intent_overridden_at
        : prev.intentOverriddenAt,
    outcome:             outcome            ?? prev.outcome,
    endedAt:
      typeof row.ended_at === "string" ? row.ended_at : prev.endedAt,
  };
}

export function useCallStage(callId: number | undefined): CallOrchState {
  const [state, setState] = useState<CallOrchState>(EMPTY);

  useEffect(() => {
    if (callId === undefined) {
      setState(EMPTY);
      return;
    }
    let cancelled = false;
    setState(EMPTY);

    // Initial fetch — picks up current state in case we joined mid-call.
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/calls/${callId}`);
        if (!r.ok) return;
        const d = (await r.json()) as CallRow;
        if (cancelled) return;
        setState((prev) => applyRow(prev, d));
      } catch {
        /* SSE will catch up */
      }
    })();

    // Live updates — the worker emits "call" events with the partial row
    // whenever the bot patches it. Fields ride along.
    const es = new EventSource(`${API_URL}/api/calls/${callId}/stream`);
    es.addEventListener("call", (ev) => {
      try {
        const row = JSON.parse((ev as MessageEvent).data) as CallRow;
        setState((prev) => applyRow(prev, row));
      } catch {
        /* malformed event, skip */
      }
    });
    return () => {
      cancelled = true;
      es.close();
    };
  }, [callId]);

  return state;
}
